import React, { useState } from "react";

// Profile view for fleet users (getUserProfile → app_type: "FLEET"), same look as the fleet user details

const pad2 = (n) => String(n).padStart(2, "0");
// "2026-10-31" or epoch ms → "31-10-2026"
const formatDate = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const d = new Date(/^\d+$/.test(String(value)) ? Number(value) : value);
  return isNaN(d.getTime()) ? "" : `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
};
const LICENSE_TYPES = {
  LMV: "LMV - Light Motor Vehicle",
  HMV: "HMV - Heavy Motor Vehicle",
  HGMV: "HGMV - Heavy Goods Motor Vehicle",
  HPMV: "HPMV - Heavy Passenger Motor Vehicle",
  TRANS: "Transport",
};

const card = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  overflow: "hidden",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
};
const cardHeader = {
  background: "linear-gradient(135deg, #0f4a45, #1a7a73)",
  padding: "12px 18px",
  color: "#fff",
  fontWeight: 700,
  fontSize: 14,
};

const Item = ({ label, value, wide }) => (
  <div style={{ gridColumn: wide ? "1 / -1" : undefined, minWidth: 0 }}>
    <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 500, wordBreak: "break-word" }}>{value || "—"}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ ...card, marginBottom: 16 }}>
    <div style={cardHeader}>{title}</div>
    <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "14px 20px" }}>
      {children}
    </div>
  </div>
);

const Photo = ({ url, name, size }) => {
  const [failed, setFailed] = useState(false);
  const box = { width: size, height: size, borderRadius: "50%", flexShrink: 0 };
  if (url && !failed) {
    return <img src={url} alt="" onError={() => setFailed(true)} style={{ ...box, objectFit: "cover", border: "3px solid #e6f4f2" }} />;
  }
  return (
    <div style={{ ...box, background: "#e6f4f2", color: "#0f4a45", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: size * 0.38 }}>
      {(name || "?").charAt(0).toUpperCase()}
    </div>
  );
};

export default function FleetProfile({ profile }) {
  if (!profile) return null;

  const fullName = [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(" ");
  const active = String(profile.status) !== "false";
  const isDriver = (profile.role_name || "").trim().toLowerCase() === "driver" || !!profile.license_number;
  // region_name comes as one comma-separated string ("ARARIA, ARWAL, …")
  const regions = (profile.region_name || "").split(",").map((r) => r.trim()).filter(Boolean);

  return (
    <div style={{ padding: "16px 20px", fontFamily: "'Segoe UI', sans-serif", display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
      {/* ── Left: identity card ── */}
      <div style={{ ...card, width: 280, flexShrink: 0 }}>
        <div style={{ ...cardHeader, height: 56 }} />
        <div style={{ padding: "0 20px 20px", textAlign: "center", marginTop: -46 }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Photo url={profile.attachments_path} name={profile.first_name} size={96} />
          </div>
          <div style={{ marginTop: 10, fontSize: 17, fontWeight: 700, color: "#0f3d3a" }}>{fullName || "—"}</div>
          <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 2 }}>{profile.role_name || "—"}</div>
          <div style={{ marginTop: 10 }}>
            <span style={{ background: active ? "#dcfce7" : "#f1f5f9", color: active ? "#16a34a" : "#64748b", borderRadius: 20, padding: "3px 12px", fontSize: 11.5, fontWeight: 700 }}>
              {active ? "Active" : "Inactive"}
            </span>
          </div>
          <div style={{ borderTop: "1px solid #f1f5f9", marginTop: 16, paddingTop: 14, display: "grid", gap: 10, textAlign: "left" }}>
            <Item label="User ID" value={profile.user_name} />
            <Item label="Employee ID" value={profile.employee_id} />
            <Item label="Phone" value={profile.phone_number} />
            <Item label="Email" value={profile.email} />
          </div>
        </div>
      </div>

      {/* ── Right: details ── */}
      <div style={{ flex: 1, minWidth: 320 }}>
        <Section title="Personal Details">
          <Item label="First Name" value={profile.first_name} />
          <Item label="Last Name" value={profile.last_name} />
          <Item label="Employee ID" value={profile.employee_id} />
          <Item label="Phone Number" value={profile.phone_number} />
          <Item label="Blood Group" value={profile.blood_group} />
          <Item label="Email" value={profile.email} />
        </Section>

        <Section title="Role & Access">
          <Item label="Role" value={profile.role_name} />
          <Item label="Role Category" value={profile.is_admin ? "Admin" : "User"} />
          <Item label="App Type" value={profile.app_type} />
          {/* Regions on their own full-width row, listed top-to-bottom in columns */}
          <Item label={`Region${regions.length > 1 ? ` (${regions.length})` : ""}`} wide value={
            regions.length ? (
              <ol style={{
                // no fixed height: with CSS columns a max-height pushes extra columns off to the right
                margin: 0, padding: "8px 12px 8px 30px", columnWidth: 170, columnGap: 24,
                background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8,
              }}>
                {regions.map((r) => (
                  <li key={r} style={{ breakInside: "avoid", fontSize: 12.5, color: "#1e293b", padding: "3px 0" }}>{r}</li>
                ))}
              </ol>
            ) : ""
          } />
        </Section>

        {isDriver && (
          <Section title="Driver Details">
            <Item label="License Number" value={profile.license_number} />
            <Item label="License Type" value={LICENSE_TYPES[profile.license_type] || profile.license_type} />
            <Item label="License Expiry" value={formatDate(profile.license_expiry)} />
            <Item label="License Photo" value={
              profile.license_file_path ? (
                <a href={profile.license_file_path} target="_blank" rel="noreferrer">
                  <img src={profile.license_file_path} alt="License" style={{ height: 70, borderRadius: 6, border: "1px solid #e2e8f0" }} />
                </a>
              ) : ""
            } />
          </Section>
        )}

        <Section title="Emergency Contact">
          <Item label="Contact Name" value={profile.emergency_contact_name} />
          <Item label="Contact Phone" value={profile.emergency_contact_phone} />
        </Section>

      </div>
    </div>
  );
}
